import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LuSearch } from 'react-icons/lu';
import TraderLayout from '../../components/TraderLayout/TraderLayout';
import BuyerOrderDrawer from '../../components/BuyerOrderDrawer/BuyerOrderDrawer';
import { fetchMyOrders } from '../../lib/directus';
import styles from './BuyerDashboard.module.css';
import BarnIcon from '../../icons/BarnIcon';

const ESCROW_STATUS_LABEL = {
  pending_approval:  'Awaiting approval',
  awaiting_finance:  'Awaiting finance',
  finance_approved:  'Paid',
  escrow_locked:     'Paid',
  transport_sourced: 'Paid',
  in_transit:        'In transit',
  delivered:         'Delivered',
  released:          'Delivered',
  rejected:          'Rejected',
};

function mapOrder(o) {
  const batch     = o.batch || {};
  const qty       = parseFloat(batch.quantity) || 0;
  const unitPrice = parseFloat(batch.unit_price) || 0;
  const amount    = qty * unitPrice;
  const status    = ESCROW_STATUS_LABEL[o.escrow_status] || o.escrow_status || 'Awaiting approval';

  return {
    id:               o.id,
    orderId:          o.order_id ? `#${o.order_id}` : `#ORD-${String(o.id).padStart(6, '0')}`,
    commodity:        batch.commodity || '-',
    quantity:         qty ? `${qty}${batch.unit_of_measure ? ' ' + batch.unit_of_measure : ' MT'}` : '-',
    grade:            batch.grade || '-',
    seller:           batch.seller?.company_name || '-',
    status,
    escrow_status:    o.escrow_status || 'pending_approval',
    fulfillment_status: o.fulfillment_status || '',
    amount:           amount ? `R${amount.toLocaleString()}` : null,
    stockValue:       amount ? `R${amount.toLocaleString()}` : 'R0',
    deliveryLocation: o.delivery_location || '-',
    proof_of_payment: o.proof_of_payment || null,
    date_created:     o.date_created,
  };
}

export default function BuyerDashboard() {
  const navigate = useNavigate();
  const [orders, setOrders]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeOrder, setActive]  = useState(null);

  const load = useCallback(() => {
    fetchMyOrders()
      .then(data => setOrders(data.map(mapOrder)))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const activeCount  = orders.filter(o => o.escrow_status === 'in_transit').length;
  const pendingCount = orders.filter(o => o.escrow_status === 'awaiting_finance').length;
  const inTransit    = orders.filter(o => o.escrow_status === 'in_transit').length;

  return (
    <TraderLayout>
      <div className={styles.page}>
        {/* Page header */}
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Dashboard</h1>
          <button className={styles.browseBtn} onClick={() => navigate('/marketplace')}>
            <LuSearch size={14} />
            Browse marketplace
          </button>
        </div>

        {/* Stat cards */}
        <div className={styles.statsRow}>
          <div className={styles.statCard}>
            <p className={styles.statValue}>{String(activeCount).padStart(2, '0')}</p>
            <p className={styles.statLabel}>active orders</p>
          </div>
          <div className={styles.statCard}>
            <p className={styles.statValue}>{String(pendingCount).padStart(2, '0')}</p>
            <p className={styles.statLabel}>awaiting finance</p>
          </div>
          <div className={styles.statCard}>
            <p className={styles.statValue}>{String(inTransit).padStart(2, '0')}</p>
            <p className={styles.statLabel}>in transit</p>
          </div>
          <div className={styles.statCard}>
            <p className={styles.statValue}>{String(orders.length).padStart(2, '0')}</p>
            <p className={styles.statLabel}>total orders</p>
          </div>
        </div>

        {/* Recent orders */}
        <p className={styles.sectionTitle}>Recent orders</p>
        {loading ? (
          <p style={{ color: 'var(--secondary-text)', fontSize: 14 }}>Loading...</p>
        ) : orders.length === 0 ? (
          <p style={{ color: 'var(--secondary-text)', fontSize: 14 }}>No orders yet.</p>
        ) : (
          <div className={styles.ordersGrid}>
            {orders.map(order => (
              <div key={order.id} className={styles.tripCard} onClick={() => setActive(order)} style={{ cursor: 'pointer' }}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardTop}>
                    <span className={styles.loadIdText}>{order.orderId}</span>
                    <span className={`${styles.badge} ${
                      order.escrow_status === 'pending_approval' ? styles.badgePending :
                      order.escrow_status === 'awaiting_finance' ? styles.badgePending :
                      order.escrow_status === 'rejected'         ? styles.badgePending :
                      order.status === 'Delivered'               ? styles.badgeReleased :
                      styles.badgeActive
                    }`}>{order.status}</span>
                  </div>
                </div>
                <div className={styles.cardBody}>
                  <p className={styles.orderCommodity}>{order.commodity} • {order.quantity} • {order.grade}</p>
                  <div className={styles.orderSeller}>
                    <BarnIcon size={14} color="var(--default-text)" />
                    <span>{order.seller}</span>
                  </div>
                  <div className={styles.cardDivider} />
                  <p className={styles.orderAmount}>{order.amount}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <BuyerOrderDrawer
        order={activeOrder}
        onClose={() => setActive(null)}
        onUpdated={() => { load(); setActive(null); }}
      />
    </TraderLayout>
  );
}
